import { AxiosResponse } from "axios";
import { IOptions } from "../../api-sdk";
import BaseService from "../baseService";
import { IGetTvodStatusResponse, ITvodHistoryQuery, ITvodHistoryResponse } from "./tvod.content.types";
import { IPaginatedResponse } from "../../../types";
import qs from "qs";
import { filterNonNull } from "../../../utils";

/**
 * Service for managing TVOD (Transactional Video on Demand) content.
 */
class TvodContentService extends BaseService {
  constructor(options: IOptions) {
    super(options);
  }

  /**
   * Synchronizes TVOD content for management.
   *
   * @param content_id The ID of the content to synchronize.
   * @returns A promise that resolves to the server's response containing a success message.
   */
  async syncTvodContent(
    content_id: string,
  ): Promise<AxiosResponse<{ message: string }>> {
    return this.request({
      method: "POST",
      url: `/api/v1/content/tvod/manage/${content_id}`,
    });
  }

  /**
   * Retrieves the TVOD status for a specific content.
   *
   * @param content_id The ID of the content to check the status for.
   * @returns A promise that resolves to the server's response containing the TVOD status.
   */
  async getTvodStatus(
    content_id: string,
  ): Promise<AxiosResponse<IGetTvodStatusResponse>> {
    return this.request({
      method: "GET",
      url: `/api/v1/content/tvod/status/${content_id}`,
    });
  }

  /**
   * Retrieves a paginated list of TVOD (buy/rent) purchases for the authenticated user.
   *
   * Calls `GET /api/v1/content/tvod/purchase/history` with the given query string.
   * Undefined and null query values are omitted from the URL (see {@link filterNonNull}).
   *
   * @template C - Shape of each item's `content_id` when populated (default: string ID).
   * @template U - Shape of each item's `user_id` when populated (default: string ID).
   * @param query - Pagination, sorting, search, and population options. Pass `{}` to use API defaults.
   * @returns Axios response whose `data` is {@link IPaginatedResponse} of {@link ITvodHistoryResponse}.
   */
  async getTvodPurchaseHistory<C = string, U = string>(
    query: ITvodHistoryQuery,
  ): Promise<AxiosResponse<IPaginatedResponse<ITvodHistoryResponse<C, U>>>> {
    return this.request({
      method: "GET",
      url: `/api/v1/content/tvod/purchase/history?${qs.stringify(filterNonNull(query || {}))}`,
    });
  }
}

export default TvodContentService;
